// Argus — My Profile modal cu change password form.
// Bloc v1.0.10 #A — 2026-05-07.

import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, Box, Typography, CircularProgress,
} from '@mui/material';
import { useAdminModalsStore } from '../../store/adminModalsStore';
import { useAuthStore } from '../../lib/auth';
import { changePasswordRequest } from '../../lib/api';

export function MyProfileModal(): JSX.Element {
  const open = useAdminModalsStore((s) => s.myProfileOpen);
  const close = useAdminModalsStore((s) => s.closeMyProfile);
  const user = useAuthStore((s: { user: { username?: string; role?: string } | null }) => s.user);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm(''); setError(null); setSuccess(false);
  };
  const handleClose = () => { reset(); close(); };

  const handleSubmit = async () => {
    setError(null); setSuccess(false);
    if (!current || !next || !confirm) { setError('Toate câmpurile sunt obligatorii.'); return; }
    if (next.length < 8) { setError('Parola nouă trebuie ≥ 8 caractere.'); return; }
    if (next !== confirm) { setError('Parolele noi nu se potrivesc.'); return; }
    if (current === next) { setError('Parola nouă trebuie să fie diferită de cea curentă.'); return; }
    setSubmitting(true);
    try {
      await changePasswordRequest(current, next);
      setSuccess(true);
      setTimeout(() => { handleClose(); }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Schimbare parolă eșuată.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>My profile</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Username</Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>{user?.username ?? '—'}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Role</Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>{user?.role ?? '—'}</Typography>
        </Box>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Change password</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>Parola schimbată cu succes.</Alert>}
          <TextField
            label="Current password"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            fullWidth
            margin="dense"
            disabled={submitting}
            autoComplete="current-password"
          />
          <TextField
            label="New password (min 8 chars)"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            fullWidth
            margin="dense"
            disabled={submitting}
            autoComplete="new-password"
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
            margin="dense"
            disabled={submitting}
            autoComplete="new-password"
          />
          {/* hidden submit pentru a permite Enter key în form. */}
          <button type="submit" style={{ display: 'none' }} aria-hidden />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Close</Button>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          disabled={submitting || success}
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          {submitting ? 'Saving...' : 'Change password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
